import React, { useState, useEffect, useRef } from 'react';
import { CircularProgress } from '@mui/material';
import { IoSend, IoTrash, IoCreate, IoClose, IoChatbubbles } from 'react-icons/io5';
import api from '../api/axios';
import styles from './Chat.module.css';

interface ChatMessage {
  id: number;
  userId: number;
  username: string;
  avatarUrl?: string;
  message: string;
  attachments?: string; // JSON string of attachment URLs
  createdAt: string;
  isEdited: boolean;
  isDeleted: boolean;
}

interface ChatProps {
  currentUserId?: number;
  isAdmin?: boolean;
}

const Chat: React.FC<ChatProps> = ({ currentUserId, isAdmin = false }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [channelName, setChannelName] = useState('general');
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const previousMessageCountRef = useRef<number>(0);
  const isUserScrolledUpRef = useRef<boolean>(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleScroll = () => {
    if (!messagesContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50; // 50px threshold
    isUserScrolledUpRef.current = !isAtBottom;
  };

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await api.get('/chat/messages?limit=100');
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChatInfo = async () => {
    try {
      const response = await api.get('/chat/info');
      if (response.data.channelName) {
        setChannelName(response.data.channelName);
      }
    } catch (error) {
      console.error('Error fetching chat info:', error);
    }
  };

  useEffect(() => {
    fetchChatInfo();
    fetchMessages();
    
    // Poll for new messages every 3 seconds
    const interval = setInterval(fetchMessages, 3000);
    
    // Add scroll listener
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }
    
    return () => {
      clearInterval(interval);
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    // Only scroll if new messages were added and user is at bottom
    if (messages.length > previousMessageCountRef.current && !isUserScrolledUpRef.current) {
      scrollToBottom();
    }
    previousMessageCountRef.current = messages.length;
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('📤 Send button clicked!', { newMessage, sending });
    
    if (!newMessage.trim() || sending) {
      console.log('❌ Message blocked:', { isEmpty: !newMessage.trim(), alreadySending: sending });
      return;
    }

    try {
      console.log('🚀 Sending message to API...', { message: newMessage.trim() });
      setSending(true);
      const response = await api.post('/chat/send', {
        message: newMessage.trim()
      });
      console.log('✅ Message sent successfully!', response.data);
      
      setNewMessage('');
      // Fetch messages immediately after sending
      await fetchMessages();
    } catch (error: any) {
      console.error('💥 Error sending message:', error);
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      alert(error.response?.data || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteMessage = async (messageId: number) => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }

    try {
      await api.delete(`/chat/messages/${messageId}`);
      await fetchMessages();
    } catch (error: any) {
      console.error('Error deleting message:', error);
      alert(error.response?.data || 'Failed to delete message');
    }
  };

  const handleEditMessage = (messageId: number, currentText: string) => {
    setEditingMessageId(messageId);
    setEditingText(currentText);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditingText('');
  };

  const handleSaveEdit = async (messageId: number) => {
    if (!editingText.trim()) {
      return;
    }

    try {
      await api.put(`/chat/messages/${messageId}`, {
        message: editingText.trim()
      });
      setEditingMessageId(null);
      setEditingText('');
      await fetchMessages();
    } catch (error: any) {
      console.error('Error editing message:', error);
      alert(error.response?.data || 'Failed to edit message');
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div className={styles.chatContainer}>
      {/* Header */}
      <div className={styles.chatHeader}>
        <div className={styles.headerContent}>
          <IoChatbubbles className={styles.headerIcon} />
          <div>
            <h2 className={styles.headerTitle}>Community Chat</h2>
            <p className={styles.headerSubtitle}>Synced with Discord</p>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className={styles.infoBanner}>
        <span className={styles.infoIcon}>💡</span>
        <span>This chat is connected to our Discord <strong>#{channelName}</strong> channel. You can chat here or join us on Discord for the full experience!</span>
      </div>

      {/* Messages */}
      <div className={styles.messagesContainer} ref={messagesContainerRef}>
        {loading && messages.length === 0 ? (
          <div className={styles.loadingContainer}>
            <CircularProgress size={40} />
          </div>
        ) : messages.length === 0 ? (
          <div className={styles.emptyState}>
            <IoChatbubbles className={styles.emptyIcon} />
            <p className={styles.emptyText}>No messages yet</p>
            <p className={styles.emptySubtext}>Be the first to say something!</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isOwnMessage = msg.userId === currentUserId;
            const canDelete = isOwnMessage || isAdmin;

            return (
              <div key={msg.id} className={styles.messageRow}>
                <img 
                  src={msg.avatarUrl && (msg.avatarUrl.startsWith('http://') || msg.avatarUrl.startsWith('https://')) 
                    ? msg.avatarUrl 
                    : msg.avatarUrl 
                      ? `https://storage.tficorg.org/${msg.avatarUrl}` 
                      : 'https://ui-avatars.com/api/?name=' + encodeURIComponent(msg.username) + '&background=3b82f6&color=fff'} 
                  alt={msg.username}
                  className={styles.messageAvatar}
                  onError={(e) => {
                    e.currentTarget.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(msg.username) + '&background=3b82f6&color=fff';
                  }}
                />
                <div className={styles.messageContent}>
                  <div className={styles.messageHeader}>
                    <span className={styles.messageUsername}>{msg.username}</span>
                    <span className={styles.messageTime}>
                      {formatTime(msg.createdAt)}
                      {msg.isEdited && <span className={styles.editedLabel}> (edited)</span>}
                    </span>
                  </div>
                  {editingMessageId === msg.id ? (
                    <div className={styles.editContainer}>
                      <textarea
                        className={styles.editInput}
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        maxLength={2000}
                        rows={2}
                        autoFocus
                      />
                      <div className={styles.editActions}>
                        <button
                          className={styles.saveButton}
                          onClick={() => handleSaveEdit(msg.id)}
                          disabled={!editingText.trim()}
                        >
                          Save
                        </button>
                        <button
                          className={styles.cancelButton}
                          onClick={handleCancelEdit}
                        >
                          <IoClose size={16} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className={styles.messageText}>
                        {msg.message}
                      </div>
                      {msg.attachments && (() => {
                        try {
                          const attachmentUrls = JSON.parse(msg.attachments);
                          return (
                            <div className={styles.attachments}>
                              {attachmentUrls.map((url: string, index: number) => (
                                <a 
                                  key={index} 
                                  href={url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className={styles.attachmentLink}
                                >
                                  <img 
                                    src={url} 
                                    alt={`Attachment ${index + 1}`} 
                                    className={styles.attachmentImage}
                                  />
                                </a>
                              ))}
                            </div>
                          );
                        } catch (e) {
                          return null;
                        }
                      })()}
                    </>
                  )}
                </div>
                <div className={styles.messageActions}>
                  {isOwnMessage && editingMessageId !== msg.id && (
                    <button
                      className={styles.editButton}
                      onClick={() => handleEditMessage(msg.id, msg.message)}
                      title="Edit message"
                    >
                      <IoCreate size={16} />
                    </button>
                  )}
                  {canDelete && editingMessageId !== msg.id && (
                    <button
                      className={styles.deleteButton}
                      onClick={() => handleDeleteMessage(msg.id)}
                      title="Delete message"
                    >
                      <IoTrash size={16} />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <form onSubmit={handleSendMessage} className={styles.inputContainer}>
        <div className={styles.inputWrapper}>
          <textarea
            className={styles.messageInput}
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage(e);
              }
            }}
            disabled={sending}
            maxLength={2000}
            rows={1}
          />
          <button
            type="submit"
            className={styles.sendButton}
            disabled={!newMessage.trim() || sending}
          >
            {sending ? <CircularProgress size={20} /> : <IoSend />}
          </button>
        </div>
        <div className={styles.characterCount}>
          {newMessage.length}/2000 characters
        </div>
      </form>
    </div>
  );
};

export default Chat;
